import React, { useCallback, useEffect, useState } from 'react';

import PageHeader from '../../components/PageHeader.jsx';
import Loader, { ErrorBox } from '../../components/Loader.jsx';
import SortableTh from '../../components/SortableTh.jsx';
import { useTableSort } from '../../lib/tableSort.js';
import { SuperAdminAPI } from '../../lib/api';
import { notify } from '../../lib/notifications';


/**
 * Super Admin → Default Modules.
 *
 * Chooses which product modules every newly created organisation
 * receives.  Existing organisations are not changed — use the
 * per-organisation Modules screen for that.
 */
export default function ModuleDefaults() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await SuperAdminAPI.moduleDefaults.get();
      setModules(payload.modules || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const toggle = (key) => {
    setModules((current) => current.map((item) => (
      item.key === key ? { ...item, is_default: !item.is_default } : item
    )));
  };

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const keys = modules.filter((item) => item.is_default).map((item) => item.key);
      const payload = await SuperAdminAPI.moduleDefaults.save(keys);
      setModules(payload.modules || []);
      notify.success('Default modules saved. New organisations will receive this set.');
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }, [modules]);

  if (loading && modules.length === 0) return <Loader label="Loading default modules…" />;

  return (
    <>
      <PageHeader
        title="🧩 Default modules"
        subtitle="Modules activated automatically for every new organisation. Super-admin can add or remove modules for an organisation later."
        actions={
          <>
            <button type="button" className="ty-btn ty-btn--ghost" onClick={reload} disabled={loading || saving}>
              ↻ Refresh
            </button>
            <button type="button" className="ty-btn" onClick={save} disabled={saving || loading}>
              {saving ? 'Saving…' : 'Save defaults'}
            </button>
          </>
        }
      />
      <ErrorBox error={error} />
      <ModuleToggleList modules={modules} onToggle={toggle} />
    </>
  );
}


export function ModuleToggleList({ modules, onToggle, activeKey = 'is_default' }) {
  const { sortedRows, sort, toggleSort } = useTableSort(modules, {
    getValue: (item, key) => {
      if (key === 'module') return item.label || item.key;
      if (key === 'active') return item[activeKey] ? 1 : 0;
      return item[key];
    },
  });
  if (!modules.length) {
    return <div className="ty-empty">No product modules in the catalogue.</div>;
  }
  return (
    <div className="ty-card" style={{ padding: 0, overflow: 'hidden' }}>
      <table className="ty-table">
        <thead>
          <tr>
            <SortableTh label="Module" sortKey="module" activeKey={sort.key} direction={sort.dir} onSort={toggleSort} />
            <SortableTh label="Active" sortKey="active" activeKey={sort.key} direction={sort.dir} onSort={toggleSort} style={{ width: 140 }} />
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((item) => (
            <tr key={item.key}>
              <td>
                <strong>{item.label}</strong>
                <div style={{ fontSize: 13, color: 'var(--ty-muted)', marginTop: 2 }}>
                  {item.key}
                  {item.tenant_only ? ' · organisation workspaces only' : ''}
                </div>
                {item.note && (
                  <div style={{ fontSize: 13, color: 'var(--ty-muted)', marginTop: 4 }}>
                    {item.note}
                  </div>
                )}
              </td>
              <td>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!item[activeKey]}
                    onChange={() => onToggle(item.key)}
                  />
                  {item[activeKey] ? 'On' : 'Off'}
                </label>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
